import type { Enums } from "../types";

/**
 * The states an order may still be cancelled from.
 *
 * These are exactly the states in which no pick has been booked. `insertSuborderPick` refuses any
 * suborder that is not `picking`, an order is `pending` only while every suborder still is, and
 * `open` only while it has no suborders at all. That is what makes a cancellation from here a
 * clean withdrawal: every line still owes its full quantity, so dropping the order's claim gives
 * the shelf back exactly what the order took and nothing else.
 *
 * Past this point the goods have started moving. A `picking` or `in-progress` order has stock
 * recorded against `orderArticles."pickedQuantity"` that has physically left its shelf, and
 * cancelling would release the reservation while that stock stays gone — picked goods no order
 * accounts for.
 */
const CANCELLABLE_ORDER_STATES: Array<Enums<"orderState">> = ["open", "pending"];

/**
 * Whether an order in this state may still be cancelled.
 *
 * The single definition of that rule, shared by the server guard and by both clients' cancel
 * actions, so a button cannot offer what the handler refuses.
 */
export function canCancelOrder(state: Enums<"orderState">) {
	return CANCELLABLE_ORDER_STATES.includes(state);
}

/**
 * Whether an order in this state may be restored.
 *
 * Only a cancelled one. Restoring writes `pending` over every suborder, so aimed at a live order
 * it would throw away a picker's progress through the round.
 */
export function canRestoreOrder(state: Enums<"orderState">) {
	return state === "canceled";
}
